import classNames from 'classnames'
import {debounce} from 'lodash'
import * as React from 'react'

import Doodle from '@/assets/specify/IconDoodleArrowRight'
import {handleHubspotResponse, makeEditable} from '@/common/utils'
import Content from '@/components/Content'
import {isValidEmail} from '@/components/Form/helpers'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import Image from '@/components/Image'
import RichText from '@/components/RichText/Richtext'

const Form = ({
  backgroundColors,
  headingTag,
  heading,
  content,
  image,
  portalId,
  formId,
  firstNamePlaceholder,
  lastNamePlaceholder,
  emailPlaceholder,
  emailNoValidLabel,
  companyNamePlaceholder,
  sessionLabel,
  sessions,
  submitButton,
  loadingMessage,
  successMessage: successMessagePlaceholder,
  siretMessage,
  dataCollect,
  title,
  _editable,
}) => {
  const HeadingTag = headingTag || 'p'

  const [isLoading, setIsLoading] = React.useState(false)
  const [successMessage, setSuccessMessage] = React.useState()
  const [isValid, setIsValid] = React.useState(true)
  const [companyValue, setCompanyValue] = React.useState('')
  const [siretNumber, setSiretNumber] = React.useState('')
  const [isBadAdress, setIsBadAdress] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState()
  const [searchCompanyResult, setSearchCompanyResult] = React.useState([])
  const companyInput = React.useRef()
  const [color, setColor] = React.useState('#bea9c2')

  const handleChange = (event) => {
    setColor(event.target.value ? '#280c2d' : '#bea9c2')
  }

  const searchCompany = async (input) => {
    const company = input.target.value
    const result = await fetch(`/crm_siret?term=` + company)
    const resultData = await result.json()

    setSearchCompanyResult(resultData.results)
  }

  const debounceSearch = debounce(searchCompany, 300)

  const selectCompany = (result) => {
    companyInput.current.value = result
    setSearchCompanyResult([])
  }

  const checkValidity = (result) => {
    setCompanyValue(result.target.value)
  }

  React.useEffect(() => {
    if (companyValue !== '' && siretNumber === '') {
      setIsValid(false)
    } else {
      setIsValid(true)
    }
  }, [companyValue, siretNumber])

  async function handleSubmit(e) {
    e.preventDefault()

    try {
      setIsLoading(true)
      setSuccessMessage()
      setErrorMessage()

      const form = e.target
      const formData = new FormData(form)
      const pageUrl = window.location.href

      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)

      const email = formData.get('email')

      if (!(await isValidEmail(email))) {
        setIsBadAdress(true)
        const input = form.querySelector("input[name='email']")
        if (input) input.focus()
        return
      }

      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
        {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: [
              {
                objectTypeId: '0-1',
                name: 'firstname',
                value: formData.get('firstname') || '',
              },
              {
                objectTypeId: '0-1',
                name: 'lastname',
                value: formData.get('lastname') || '',
              },
              {
                objectTypeId: '0-1',
                name: 'email',
                value: email || '',
              },
              {
                objectTypeId: '0-2',
                name: 'name',
                value: formData.get('name') || '',
              },
              {
                objectTypeId: '0-2',
                name: 'siret__c',
                value: formData.get('siret__c') || '',
              },
              {
                objectTypeId: '0-1',
                name: 'identifiant_de_la_session_de_webinaire',
                value: formData.get('identifiant_de_la_session_de_webinaire') || '',
              },
              {
                name: 'utm_campaign',
                value: urlParams.get('utm_campaign') || formData.get('utm_campaign') || '',
              },
              {
                name: 'utm_source',
                value: urlParams.get('utm_source') || formData.get('utm_source') || '',
              },
              {
                name: 'utm_term',
                value: urlParams.get('utm_term') || formData.get('utm_term') || '',
              },
            ],
            context: {
              pageUri: pageUrl,
              pageName: title,
            },
          }),
        }
      )

      if (response.ok) {
        await handleHubspotResponse(response, (message) =>
          setSuccessMessage(message || successMessagePlaceholder)
        )
      } else {
        const {errors = []} = await response.json()
        throw Error(errors[0]?.message)
      }
    } catch (error) {
      setErrorMessage(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section
      {...makeEditable(_editable)}
      className={classNames(
        'bg-yellow-200 bg-[radial-gradient(circle_at_bottom,#F6FF8C,transparent,transparent,transparent,transparent)] py-16',
        backgroundColors.current
      )}
    >
      <Grid className='gap-y-10'>
        <div className='col-span-full md:col-start-2 md:col-end-6 lg:order-2 lg:col-start-7 lg:col-end-13'>
          <HeadingTag className='mb-6'>
            <Heading heading={heading} className='heading-s-satoshi text-center lg:text-left' />
          </HeadingTag>
          <Content content={content} className='mb-10 text-center lg:text-left' size='m' />
          <div className='max-w-[theme(spacing.96)]'>
            <form
              onSubmit={handleSubmit}
              className='flex w-full max-w-md flex-col'
              onKeyDown={(e) => {
                e.key === 'Enter' && e.preventDefault()
              }}
            >
              <input
                className='body-m-medium mb-4 w-full rounded-lg border border-transparent px-2 py-3 text-purple-900 transition-all duration-500 placeholder:text-purple-300 focus:border-violet-600 focus:outline-0 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]'
                type='text'
                name='firstname'
                required
                placeholder={firstNamePlaceholder}
              />
              <input
                className='body-m-medium mb-4 w-full rounded-lg border border-transparent px-2 py-3 text-purple-900 transition-all duration-500 placeholder:text-purple-300 focus:border-violet-600 focus:outline-0 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]'
                type='text'
                name='lastname'
                required
                placeholder={lastNamePlaceholder}
              />
              <input
                className={classNames(
                  'body-m-medium mb-4 w-full rounded-lg border border-transparent px-2 py-3 text-purple-900 transition-all duration-500 placeholder:text-purple-300 focus:border-violet-600 focus:outline-0 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]',
                  {
                    'border-red-400 bg-[rgba(255,0,0,0.08)]': isBadAdress,
                  }
                )}
                type='email'
                name='email'
                required
                placeholder={emailPlaceholder}
                onChange={() => setIsBadAdress(false)}
              />
              {isBadAdress && <div className='mb-2 text-red-400'>{emailNoValidLabel}</div>}
              <div className='relative rounded-lg bg-white'>
                <input
                  className={classNames(
                    'body-m-medium w-[calc(100%-150px)] text-ellipsis	rounded-lg border border-transparent px-2 py-3 text-purple-900 shadow-[0px_12px_12px_rgba(0,0,0,0.02)] transition-all duration-500 placeholder:text-purple-300 focus:border-violet-600 focus:outline-0',
                    {
                      'border-red-400 focus:border-red-400 ': !isValid,
                    }
                  )}
                  type='text'
                  name='name'
                  autoComplete='off'
                  required
                  placeholder={companyNamePlaceholder}
                  onKeyUp={debounceSearch}
                  ref={companyInput}
                  onBlur={checkValidity}
                />
                {siretNumber !== '' && (
                  <span className='body-s-medium absolute right-2 top-1/2 flex-1 -translate-y-1/2 whitespace-nowrap text-violet-400'>
                    N° {siretNumber}
                  </span>
                )}
              </div>
              {!isValid && <div className='mt-2 text-red-400'>{siretMessage}</div>}
              <input
                className='body-m-medium mb-4 w-full rounded-lg border border-transparent px-2 py-3 transition-all duration-500 focus:border-violet-600 focus:outline-0 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]'
                type='hidden'
                name='siret__c'
                required
                value={siretNumber}
              />
              <input type='hidden' name='utm_campaign' />
              <input type='hidden' name='utm_source' />
              <input type='hidden' name='utm_term' />
              <div id='search-company-result' className='relative'>
                <ul className='absolute z-10 max-h-60 w-full overflow-auto rounded-lg md:shadow-[0px_12px_12px_rgba(0,0,0,0.04)]'>
                  {searchCompanyResult?.map((result) => {
                    return (
                      <li
                        key={result.siret}
                        className='body-m-medium w-full cursor-pointer border border-transparent bg-white p-2 transition-all duration-500 last:rounded-b-lg'
                        onClick={() => {
                          selectCompany(result.name)
                          setSiretNumber(result.siret)
                        }}
                      >
                        <div className='flex w-full justify-between rounded-lg px-2 py-3 hover:bg-yellow-400'>
                          <span className='max-w-1/2 block flex-1'>{result.name}</span>
                          <span className='body-s-medium flex-1 whitespace-nowrap text-violet-400'>
                            N° Siret {result.siret}
                          </span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
              {sessions && sessions.length > 0 && (
                <>
                  <label className='body-l-bold my-4 w-full text-center'>{sessionLabel}</label>

                  {sessions.length === 1 ? (
                    <>
                      <input
                        hidden
                        type='text'
                        name='identifiant_de_la_session_de_webinaire'
                        value={sessions[0].value}
                      />
                      <span className='body-m-medium w-full bg-transparent text-center text-purple-900'>
                        {sessions[0].date}
                      </span>
                    </>
                  ) : (
                    sessions.length > 1 && (
                      <select
                        onChange={handleChange}
                        style={{color}}
                        name='identifiant_de_la_session_de_webinaire'
                        className='body-m-medium w-full rounded-lg border border-transparent bg-white px-2 py-3 focus:border-violet-600 focus:outline-0 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]'
                      >
                        {sessions.map(({_uid, value, date}, index) => {
                          return (
                            <option
                              id={_uid}
                              selected={index === 0 ? 'selected' : ''}
                              disabled={index === 0 ? 'disabled' : ''}
                              hidden={index === 0 ? 'hidden' : ''}
                              value={value}
                              className='body-m-medium text-purple-900'
                            >
                              {date ? date : value}
                            </option>
                          )
                        })}
                      </select>
                    )
                  )}
                </>
              )}

              {dataCollect && (
                <div className='mt-4'>
                  <RichText richtext={dataCollect} />
                </div>
              )}

              <button
                as='button'
                className={classNames(
                  'misc-button-m button-m-bold misc-button-m mt-8 block rounded-full border border-purple-900 bg-purple-900 px-4 py-2 text-white transition hover:bg-transparent hover:text-purple-900',
                  {
                    'cursor-not-allowed opacity-70': isBadAdress || !isValid,
                  }
                )}
                themes='primary'
                disabled={isLoading || isBadAdress || !isValid}
              >
                {isLoading ? loadingMessage : submitButton}
              </button>
            </form>
            {!!errorMessage && (
              <p className='body-s text-beige-100 w-full text-center'>{errorMessage}</p>
            )}
            {successMessage && (
              <div className='mt-4 rounded-lg bg-violet-600 py-2'>
                <p
                  className='body-s w-full text-center  text-white'
                  dangerouslySetInnerHTML={{__html: successMessage}}
                />
              </div>
            )}
          </div>
        </div>
        <div className='lg:col-row-1 relative col-span-full overflow-visible text-violet-600 lg:col-start-1 lg:col-end-6'>
          <div className='aspect-h-9 aspect-w-16  lg:aspect-h-13 lg:aspect-w-9'>
            <Image
              {...image}
              alt=''
              ratio='16:9'
              className='rounded-2xl md:rounded-[32px]'
              sizes={{sm: 100}}
            />
          </div>
          <div className='pointer-events-none absolute bottom-50 right-[20%] hidden origin-bottom-right rotate-[70deg] -scale-x-100 lg:inline-block'>
            <Doodle />
          </div>
        </div>
      </Grid>
    </section>
  )
}

export default Form
